// The org's standard working day — one derivation, used by every screen.
//
// This existed four times with three different answers. The two copies that
// preferred `profiles.avail_hrs` contradicted the server: `avail_hrs` is
// `not null default 40`, so it is NEVER null and the `avail || org` fallback only
// fired on an explicit 0 — meaning every employee HR had not touched silently
// overrode the org standard with 40. On a 9:00-18:00 day with a 2h lunch, Org
// Settings said 7h/35h while Timesheets said 8h/40h.
//
// It is not cosmetic: NO SQL reads avail_hrs. `accrue_overtime` (when overtime
// starts), `reconcile_leave_clash` (the leave-return bands) and
// `close_stale_sessions` (the cap on a forgotten session) all use
// `day_end - day_start - lunch_mins` from org_settings. A UI that says 8h while
// the database banks overtime from 7h is telling people the wrong thing about
// their own pay.
//
// So: the standard day is the ORG's, always. `avail_hrs` is a separate concept —
// one person's contracted hours — and belongs only on the HR "Contracted hours"
// card, next to their actual hours.

/** Minutes since midnight from a stored 'HH:MM' (24h, zero-padded). */
export const hhmmToMin = (s?: string | null): number => {
  const [h, m] = (s || '0:0').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/** The columns any caller must select from org_settings. */
export type OrgHoursRow = {
  day_start?: string | null;
  day_end?: string | null;
  lunch_mins?: number | null;
  workdays?: Record<string, boolean> | null;
};

export type OrgHours = {
  /** Net hours in a standard working day (span minus lunch). */
  perDay: number;
  /** perDay × the number of configured working days. */
  perWeek: number;
  /** How many days a week the org works. */
  workdayCount: number;
};

// Matches the server's fallback: `if std is null or std <= 0 then std := 28800`
// (overtime.sql:62, leave_clash.sql, stale_sessions.sql) and a 5-day week.
const FALLBACK_PER_DAY = 8;
const FALLBACK_WORKDAYS = 5;

export const orgHoursFrom = (o?: OrgHoursRow | null): OrgHours => {
  const workdayCount = Object.values(o?.workdays ?? {}).filter(Boolean).length || FALLBACK_WORKDAYS;
  const span = hhmmToMin(o?.day_end) - hhmmToMin(o?.day_start) - (o?.lunch_mins ?? 0);
  // A zero or inverted span means the org row is missing or misconfigured; fall
  // back to the same 8h the server does rather than showing a 0h standard.
  const perDay = span > 0 ? span / 60 : FALLBACK_PER_DAY;
  return { perDay, perWeek: perDay * workdayCount, workdayCount };
};

export const DEFAULT_ORG_HOURS: OrgHours = {
  perDay: FALLBACK_PER_DAY,
  perWeek: FALLBACK_PER_DAY * FALLBACK_WORKDAYS,
  workdayCount: FALLBACK_WORKDAYS,
};

// ── One person's day ─────────────────────────────────────────────────────────
//
// The mirror of SQL `person_standard_seconds()`
// (20260811100000_person_contract_hours.sql). `profiles.contract_hrs_per_day` is
// the person's contracted day; NULL means "use the org's".
//
// This is the ONE thing `avail_hrs` was not allowed to be, and the difference is
// the whole reason the new column is nullable: `avail_hrs` is `not null default
// 40`, so its `avail || org` fallback only ever fired on an explicit 0 and every
// untouched employee silently overrode the org standard. A null-ish check here,
// against a genuinely nullable column, cannot repeat that.
//
// `> 0` rather than `??` alone: the CHECK constraint forbids 0, but a 0 arriving
// from a stale cache or a hand-edited row must mean "unset", never "a zero-hour
// day" — which downstream would divide by zero or bank overtime from the first
// second. The SQL's `where contract_hrs_per_day > 0` says the same.
export const personDay = (
  contractHrsPerDay: number | null | undefined,
  org: OrgHours = DEFAULT_ORG_HOURS,
): number => (contractHrsPerDay != null && contractHrsPerDay > 0 ? contractHrsPerDay : org.perDay);

// Deliberately no `personWeek`. A person's week is `personDay × the days they are
// actually expected to work that week` (lib/contract.ts) — holidays and approved
// leave included. A flat `personDay × workdayCount` would reintroduce exactly the
// `avail × 52` fiction this change deletes from the HR card.
